/**
 * pages/AdminBusinessUnits.tsx — the scope-lifecycle module: which business units exist, what each one
 * points at, and whether it is actually working.
 *
 * **This screen is scopeless, and that is the whole point.** Every other administrative screen is read
 * *within* a business unit; this one is read when there may not be one yet. A fresh deployment has a
 * schema, the baseline groups and one admitted superuser — and no units at all, so `accessibleScopes` is
 * empty and every scoped screen correctly refuses to draw. Without a scopeless route here, the first
 * administrator of a new deployment has no way to create the first unit and the install is deadlocked.
 *
 * **What the viewer may do is not what this screen decides.** `canManage` is the superuser and nobody
 * else, because creating and editing a scope is absent from `server/permissions/delegation.ts` and
 * absence there means superuser-only. A delegated administrator holding the `business_units` section sees
 * the list and no buttons. The hidden button is UX; the 403 behind it is the control, and both exist.
 *
 * **Three fields carry more weight than they look.**
 *
 *   * `slug` is the ENVIRONMENT-VARIABLE SUFFIX — `acme-eu` reads `GIT_TOKEN__ACME_EU`. It selects which
 *     credentials this unit uses, which is why the server refuses anything outside
 *     `^[a-z0-9]+(-[a-z0-9]+)*$` at the data layer and again in the registry: `_` is excluded so that two
 *     distinct slugs can never normalise onto one suffix and quietly share a token. It is validated here
 *     too, for a decent error message — never *instead of* there.
 *   * `workspaceRoot` is a path as the SERVER sees it, not as the browser's operator does. It is the one
 *     field a reader cannot verify by looking, so `loaded` is surfaced beside it rather than buried.
 *   * `gitHost` is a P7 floor: a Bitbucket unit cannot be handed a GitLab connector by any grant.
 *
 * `enabled` is a lifecycle switch, not a delete. There is deliberately no delete: ids are never reused,
 * because a dangling grant to a deleted scope is a grant waiting to be resurrected by id reuse.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "../lib/api";
import {
  Badge,
  Banner,
  Button,
  type Column,
  DeniedState,
  Modal,
  Page,
  Select,
  Table,
  TextField,
  useToast,
} from "../components/ui";

/* ---------------------------------------------------------------------------------------------------
 * The data
 * ------------------------------------------------------------------------------------------------- */

export type GitHost = "gitlab" | "bitbucket";

export interface AdminBusinessUnit {
  readonly id: string;
  readonly slug: string;
  readonly displayName: string;
  readonly enabled: boolean;
  readonly workspaceRoot: string;
  readonly gitHost: GitHost;
  /**
   * Whether the registry could actually resolve this row — read its `config/workspace.conf` and
   * `config/repos.manifest`. A row can be enabled and still absent from every Decision; saying so is the
   * difference between "misconfigured" and "mysteriously empty".
   */
  readonly loaded: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateBusinessUnitInput {
  readonly slug: string;
  readonly displayName: string;
  readonly workspaceRoot: string;
  readonly gitHost: GitHost;
}

export interface UpdateBusinessUnitInput {
  readonly displayName?: string;
  readonly workspaceRoot?: string;
  readonly gitHost?: GitHost;
  readonly enabled?: boolean;
}

/** The server's own warning vocabulary. Rendered, never interpreted. */
export interface BuWarning {
  readonly code: string;
  readonly message: string;
}

export interface BusinessUnitsData {
  readonly businessUnits: readonly AdminBusinessUnit[];
  /**
   * How many people are assigned to each unit, or null when the viewer may not read the roster. Used to
   * state the blast radius BEFORE disabling rather than after: the server reports the stranded count in
   * its response, which is honest but a moment too late to be a decision aid.
   */
  readonly assignedCounts: ReadonlyMap<string, number> | null;
}

export interface AdminBusinessUnitsPorts {
  readonly load: (signal: AbortSignal) => Promise<BusinessUnitsData>;
  readonly create: (input: CreateBusinessUnitInput) => Promise<void>;
  readonly update: (id: string, input: UpdateBusinessUnitInput) => Promise<readonly BuWarning[]>;
}

interface RosterRow {
  readonly confinedTo: string | null;
}

async function load(signal: AbortSignal): Promise<BusinessUnitsData> {
  const list = await api.get<{ readonly businessUnits: readonly AdminBusinessUnit[] }>(
    "/api/admin/business-units",
    { signal },
  );

  // Best effort, exactly as the roster treats groups: the count belongs to another section's grant, and
  // being refused it is a normal configuration rather than a failure of this screen.
  let assignedCounts: Map<string, number> | null = null;
  try {
    const roster = await api.get<{ readonly users: readonly RosterRow[] }>("/api/admin/users", { signal });
    assignedCounts = new Map<string, number>();
    for (const user of roster.users) {
      if (user.confinedTo === null) continue;
      assignedCounts.set(user.confinedTo, (assignedCounts.get(user.confinedTo) ?? 0) + 1);
    }
  } catch {
    assignedCounts = null;
  }

  return { businessUnits: list.businessUnits, assignedCounts };
}

const apiPorts: AdminBusinessUnitsPorts = {
  load,
  create: async (input) => {
    await api.post("/api/admin/business-units", input);
  },
  update: async (id, input) => {
    // `api.patch` resolves undefined for a 204, which this route never sends — but the type says it can,
    // and a `?? []` is cheaper than a cast that would be wrong the day it does.
    const response = await api.patch<{ readonly warnings?: readonly BuWarning[] }>(
      `/api/admin/business-units/${encodeURIComponent(id)}`,
      input,
    );
    return response?.warnings ?? [];
  },
};

/* ---------------------------------------------------------------------------------------------------
 * Validation — a courtesy, never the control
 * ------------------------------------------------------------------------------------------------- */

/** The server's pattern, restated for the error message. `server/bu/types.ts` remains the authority. */
export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** `acme-eu` → `ACME_EU`. Shown live, because the suffix is the part with consequences. */
export function envSuffix(slug: string): string {
  return slug.toUpperCase().replace(/-/g, "_");
}

export function slugProblem(slug: string, existing: readonly string[]): string | undefined {
  if (slug.length === 0) return "A slug is required.";
  if (slug.length > 64) return "Too long — 64 characters at most.";
  if (!SLUG_PATTERN.test(slug)) {
    return "Lower-case letters, digits and single hyphens only — no spaces, capitals or underscores.";
  }
  if (existing.includes(slug)) return "That slug is already taken.";
  return undefined;
}

export function pathProblem(path: string): string | undefined {
  if (path.trim().length === 0) return "A workspace root is required.";
  if (!path.startsWith("/")) return "Must be an absolute path, as the server sees it.";
  return undefined;
}

/* ---------------------------------------------------------------------------------------------------
 * The screen
 * ------------------------------------------------------------------------------------------------- */

const GIT_HOST_OPTIONS = [
  { value: "gitlab", label: "GitLab" },
  { value: "bitbucket", label: "Bitbucket" },
] as const;

const BLANK: CreateBusinessUnitInput = {
  slug: "",
  displayName: "",
  workspaceRoot: "",
  gitHost: "gitlab",
};

type Draft = {
  readonly slug: string;
  readonly displayName: string;
  readonly workspaceRoot: string;
  readonly gitHost: GitHost;
};

/** Which dialogue is open, and what it is editing. */
type Dialogue =
  | { readonly kind: "create" }
  | { readonly kind: "edit"; readonly unit: AdminBusinessUnit }
  | { readonly kind: "toggle"; readonly unit: AdminBusinessUnit };

export interface AdminBusinessUnitsProps {
  /** From `/api/me`: the `business_units` section. Without it the screen renders its denied state. */
  readonly canView: boolean;
  /**
   * Whether the viewer may CREATE or EDIT a unit — the superuser, and nobody else. Scope lifecycle is
   * absent from `server/permissions/delegation.ts`, and absence there means superuser-only.
   */
  readonly canManage: boolean;
  /** Called after a change that alters which units exist, so the session's scopes can be re-resolved. */
  readonly onChanged?: () => void;
  readonly ports?: AdminBusinessUnitsPorts;
}

export function AdminBusinessUnits({
  canView,
  canManage,
  onChanged,
  ports = apiPorts,
}: AdminBusinessUnitsProps): JSX.Element {
  const [data, setData] = useState<BusinessUnitsData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [dialogue, setDialogue] = useState<Dialogue | null>(null);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState<readonly BuWarning[]>([]);
  const toast = useToast();

  const reload = useCallback((): void => setAttempt((current) => current + 1), []);

  useEffect(() => {
    if (!canView) return;
    const controller = new AbortController();
    let live = true;
    setData(null);
    setError(null);

    ports
      .load(controller.signal)
      .then((loaded) => {
        if (live) setData(loaded);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null));
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [canView, ports, attempt]);

  const units = data?.businessUnits ?? [];
  const takenSlugs = useMemo(() => units.map((unit) => unit.slug), [units]);

  // The section decides whether the screen is drawn; the server decides whether it answers.
  if (!canView) {
    return (
      <Page title="Business units">
        <DeniedState body="Administering business units is a separate grant. Ask an administrator to add you to a group that carries the business units section." />
      </Page>
    );
  }

  const closeDialogue = (): void => {
    setDialogue(null);
    setDraft(BLANK);
  };

  const openCreate = (): void => {
    setDraft(BLANK);
    setDialogue({ kind: "create" });
  };

  const openEdit = (unit: AdminBusinessUnit): void => {
    setDraft({
      slug: unit.slug,
      displayName: unit.displayName,
      workspaceRoot: unit.workspaceRoot,
      gitHost: unit.gitHost,
    });
    setDialogue({ kind: "edit", unit });
  };

  const slugError = dialogue?.kind === "create" ? slugProblem(draft.slug, takenSlugs) : undefined;
  const pathError = pathProblem(draft.workspaceRoot);
  const nameError = draft.displayName.trim().length === 0 ? "A name is required." : undefined;
  const draftValid = slugError === undefined && pathError === undefined && nameError === undefined;

  const submitCreate = (): void => {
    if (!draftValid || busy) return;
    setBusy(true);
    ports
      .create({
        slug: draft.slug,
        displayName: draft.displayName.trim(),
        workspaceRoot: draft.workspaceRoot.trim(),
        gitHost: draft.gitHost,
      })
      .then(() => {
        toast.show(`${draft.displayName.trim()} created`, { tone: "success" });
        closeDialogue();
        reload();
        onChanged?.();
      })
      .catch((cause: unknown) => {
        toast.show(cause instanceof ApiError ? cause.userMessage : "Could not create it", { tone: "danger" });
      })
      .finally(() => setBusy(false));
  };

  const submitEdit = (unit: AdminBusinessUnit): void => {
    if (!draftValid || busy) return;
    setBusy(true);
    ports
      .update(unit.id, {
        displayName: draft.displayName.trim(),
        workspaceRoot: draft.workspaceRoot.trim(),
        gitHost: draft.gitHost,
      })
      .then((returned) => {
        toast.show(`${draft.displayName.trim()} saved`, { tone: "success" });
        setWarnings(returned);
        closeDialogue();
        reload();
        onChanged?.();
      })
      .catch((cause: unknown) => {
        toast.show(cause instanceof ApiError ? cause.userMessage : "Could not save it", { tone: "danger" });
      })
      .finally(() => setBusy(false));
  };

  const submitToggle = (unit: AdminBusinessUnit): void => {
    if (busy) return;
    setBusy(true);
    ports
      .update(unit.id, { enabled: !unit.enabled })
      .then((returned) => {
        toast.show(unit.enabled ? `${unit.displayName} disabled` : `${unit.displayName} enabled`, {
          tone: "success",
        });
        // Kept on the page rather than in a toast: a stranded-user count is the kind of thing an
        // administrator needs to still be able to read a minute later.
        setWarnings(returned);
        closeDialogue();
        reload();
        onChanged?.();
      })
      .catch((cause: unknown) => {
        toast.show(cause instanceof ApiError ? cause.userMessage : "Could not change it", { tone: "danger" });
      })
      .finally(() => setBusy(false));
  };

  const columns: readonly Column<AdminBusinessUnit>[] = [
    {
      key: "displayName",
      header: "Business unit",
      type: "text",
      value: (unit) => unit.displayName,
    },
    {
      key: "slug",
      header: "Slug",
      type: "text",
      value: (unit) => unit.slug,
      render: (unit) => (
        <code className="mono" title={`Credentials are read from …__${envSuffix(unit.slug)}`}>
          {unit.slug}
        </code>
      ),
    },
    {
      key: "status",
      header: "Status",
      type: "text",
      value: (unit) => statusLabel(unit),
      render: (unit) => <Badge tone={statusTone(unit)}>{statusLabel(unit)}</Badge>,
    },
    {
      key: "gitHost",
      header: "Git host",
      type: "text",
      value: (unit) => unit.gitHost,
      render: (unit) => (unit.gitHost === "gitlab" ? "GitLab" : "Bitbucket"),
    },
    {
      key: "workspaceRoot",
      header: "Workspace root",
      type: "text",
      value: (unit) => unit.workspaceRoot,
      render: (unit) => <code className="mono">{unit.workspaceRoot}</code>,
      hideOnMobile: true,
    },
    {
      key: "assigned",
      header: "People",
      type: "number",
      numeric: true,
      value: (unit) => data?.assignedCounts?.get(unit.id) ?? null,
      render: (unit) => {
        const count = data?.assignedCounts?.get(unit.id);
        return count === undefined ? <span className="field__hint">—</span> : <>{count}</>;
      },
    },
    ...(canManage
      ? [
          {
            key: "actions",
            actions: true,
            header: "Actions",
            sortable: false,
            render: (unit: AdminBusinessUnit) => (
              <span className="row">
                <Button variant="secondary" size="sm" onClick={() => openEdit(unit)}>
                  Edit
                </Button>
                <Button
                  variant={unit.enabled ? "danger" : "secondary"}
                  size="sm"
                  onClick={() => setDialogue({ kind: "toggle", unit })}
                >
                  {unit.enabled ? "Disable" : "Enable"}
                </Button>
              </span>
            ),
          } as Column<AdminBusinessUnit>,
        ]
      : []),
  ];

  const unresolved = units.filter((unit) => unit.enabled && !unit.loaded);

  return (
    <Page
      title="Business units"
      lede="A business unit is a scope, not a label: it decides which repositories can be read, which Confluence spaces may be written to, and whose credentials are used. Create one before assigning anybody to it."
      actions={
        canManage ? (
          <Button variant="primary" onClick={openCreate}>
            Add business unit
          </Button>
        ) : undefined
      }
    >
      {warnings.length > 0 ? (
        <Banner tone="warning">
          <span className="section">
            {warnings.map((warning) => (
              <span key={`${warning.code}:${warning.message}`}>{warning.message}</span>
            ))}
          </span>
        </Banner>
      ) : null}

      {unresolved.length > 0 ? (
        <Banner tone="danger">
          {unresolved.length === 1
            ? `${unresolved[0]?.displayName ?? "One unit"} is enabled but is not resolving, so nobody reaches it.`
            : `${unresolved.length} enabled units are not resolving, so nobody reaches them.`}{" "}
          Check that the workspace root exists on the server and holds <code className="mono">config/workspace.conf</code>{" "}
          and <code className="mono">config/repos.manifest</code>, and that the service user can read it.
        </Banner>
      ) : null}

      <Table
        caption="Business units"
        columns={columns}
        rows={units}
        rowKey={(unit) => unit.id}
        defaultSort={{ key: "displayName", direction: "ascending" }}
        titleKey="displayName"
        mobile="cards"
        loading={data === null && error === null}
        {...(error === null
          ? {}
          : {
              error: {
                message: error.userMessage,
                traceId: error.traceId,
                onRetry: reload,
              },
            })}
        empty={{
          title: "No business units yet",
          body: canManage
            ? "Nothing can be scheduled until one exists. Create the first one — you will need the absolute path, on this server, of that unit's jeeves checkout."
            : "None have been created yet. Creating one is reserved to the superuser.",
          ...(canManage
            ? {
                action: (
                  <Button variant="primary" onClick={openCreate}>
                    Add business unit
                  </Button>
                ),
              }
            : {}),
        }}
      />

      <Modal
        open={dialogue?.kind === "create"}
        title="Add a business unit"
        description="The slug cannot be changed afterwards: it is the environment-variable suffix this unit's credentials are read from."
        onClose={closeDialogue}
        // Empty: close. Half-filled: ASK. Returning false without asking would make a scrim click do
        // nothing at all, with no explanation — which reads as a broken dialog rather than a refusal.
        confirmDismiss={() =>
          (draft.slug === "" && draft.displayName === "" && draft.workspaceRoot === "") ||
          window.confirm("Discard this business unit?")
        }
        actions={
          <>
            <Button variant="ghost" onClick={closeDialogue} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitCreate} disabled={!draftValid || busy}>
              {busy ? "Creating…" : "Create"}
            </Button>
          </>
        }
      >
        <DraftFields
          draft={draft}
          onChange={setDraft}
          slugEditable
          {...(slugError === undefined ? {} : { slugError })}
          {...(nameError === undefined ? {} : { nameError })}
          {...(pathError === undefined ? {} : { pathError })}
        />
      </Modal>

      <Modal
        open={dialogue?.kind === "edit"}
        title={dialogue?.kind === "edit" ? `Edit ${dialogue.unit.displayName}` : "Edit"}
        description="The slug is fixed. Changing it would repoint this unit at a different set of credentials, which is a new unit, not an edit."
        onClose={closeDialogue}
        actions={
          <>
            <Button variant="ghost" onClick={closeDialogue} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (dialogue?.kind === "edit") submitEdit(dialogue.unit);
              }}
              disabled={!draftValid || busy}
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        <DraftFields
          draft={draft}
          onChange={setDraft}
          slugEditable={false}
          {...(nameError === undefined ? {} : { nameError })}
          {...(pathError === undefined ? {} : { pathError })}
        />
      </Modal>

      <Modal
        open={dialogue?.kind === "toggle"}
        role="alertdialog"
        title={
          dialogue?.kind === "toggle" && dialogue.unit.enabled
            ? `Disable ${dialogue.unit.displayName}?`
            : dialogue?.kind === "toggle"
              ? `Enable ${dialogue.unit.displayName}?`
              : "Change"
        }
        {...(dialogue?.kind === "toggle" ? { description: toggleBlastRadius(dialogue.unit, data) } : {})}
        onClose={closeDialogue}
        actions={
          <>
            <Button variant="ghost" onClick={closeDialogue} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant={dialogue?.kind === "toggle" && dialogue.unit.enabled ? "danger" : "primary"}
              onClick={() => {
                if (dialogue?.kind === "toggle") submitToggle(dialogue.unit);
              }}
              disabled={busy}
            >
              {dialogue?.kind === "toggle" && dialogue.unit.enabled ? "Disable" : "Enable"}
            </Button>
          </>
        }
      />
    </Page>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * Pieces
 * ------------------------------------------------------------------------------------------------- */

function statusLabel(unit: AdminBusinessUnit): string {
  if (!unit.enabled) return "Disabled";
  return unit.loaded ? "Active" : "Not resolving";
}

function statusTone(unit: AdminBusinessUnit): "success" | "danger" | "neutral" {
  if (!unit.enabled) return "neutral";
  return unit.loaded ? "success" : "danger";
}

/**
 * What disabling actually costs, stated BEFORE it happens. The server reports the same number in its
 * response, which is honest but arrives after the decision; a confirmation that cannot say how many
 * people it strands is the bare "Are you sure?" the design system exists to prevent.
 */
export function toggleBlastRadius(unit: AdminBusinessUnit, data: BusinessUnitsData | null): string {
  if (!unit.enabled) {
    return "It returns to every Decision immediately. Anyone assigned to it, and carried into it by a group, reaches it again from their next request.";
  }
  const count = data?.assignedCounts?.get(unit.id);
  const who =
    count === undefined
      ? "Anyone assigned to it"
      : count === 0
        ? "Nobody is assigned to it, so nobody"
        : count === 1
          ? "1 person is assigned to it and"
          : `${count} people are assigned to it and they`;
  return `${who} will reach no business unit at all from the next request onwards — not this one, and not any other, because an assignment narrows rather than grants. Its jobs stop running. No grant is deleted, so re-enabling restores everything.`;
}

interface DraftFieldsProps {
  readonly draft: Draft;
  readonly onChange: (draft: Draft) => void;
  readonly slugEditable: boolean;
  readonly slugError?: string;
  readonly nameError?: string;
  readonly pathError?: string;
}

function DraftFields({
  draft,
  onChange,
  slugEditable,
  slugError,
  nameError,
  pathError,
}: DraftFieldsProps): JSX.Element {
  return (
    <div className="section">
      <TextField
        label="Name"
        value={draft.displayName}
        onChange={(displayName) => onChange({ ...draft, displayName })}
        hint="How it appears in the switcher and on every screen."
        required
        {...(nameError === undefined ? {} : { error: nameError })}
      />
      <TextField
        label="Slug"
        value={draft.slug}
        onChange={(slug) => onChange({ ...draft, slug })}
        disabled={!slugEditable}
        required
        hint={
          draft.slug === ""
            ? "Lower-case letters, digits and single hyphens. This becomes the environment-variable suffix."
            : `Credentials for this unit are read from GIT_TOKEN__${envSuffix(draft.slug)}, CONFLUENCE_API_TOKEN__${envSuffix(draft.slug)}, and so on.`
        }
        {...(slugError === undefined ? {} : { error: slugError })}
      />
      <TextField
        label="Workspace root"
        value={draft.workspaceRoot}
        onChange={(workspaceRoot) => onChange({ ...draft, workspaceRoot })}
        placeholder="/opt/workspaces/acme"
        required
        hint="The absolute path, ON THE SERVER, of this unit's jeeves checkout. Only config/workspace.conf and config/repos.manifest are read, and never written."
        {...(pathError === undefined ? {} : { error: pathError })}
      />
      <Select
        label="Git host"
        value={draft.gitHost}
        options={[...GIT_HOST_OPTIONS]}
        onChange={(value) => onChange({ ...draft, gitHost: value === "bitbucket" ? "bitbucket" : "gitlab" })}
        hint="Fixed for this unit: no group grant can hand it the other host's connector."
      />
    </div>
  );
}
